/* eslint-disable max-lines */
import React, { useState } from 'react';
import { MenuPlacement } from 'react-select';
import { borderRadiusPalette } from 'utils/styleConstants';
import { useTheme } from 'providers/ThemeProvider';
import { createNormalStyles, createSmallStyles, FeedbackText, SelectWrapper } from './dropdown/styled';
import AsyncSelect from 'react-select/async';

type OptionType = {
  value: string;
  label: string;
};

export interface DialogInputProps {
  id?: string;
  loadOptions: (inputValue: string) => Promise<any[]>;
  isMulti?: boolean;
  disabled?: boolean;
  placeholder?: string;
  small?: boolean;
  onChange?: (option: any, info?: any) => void;
  onCreateOption?: (option: any) => void;
  onFocus?: () => void;
  value?: OptionType | null;
  isClearable?: boolean;
  name?: string;
  hasFeedback?: boolean;
  feedbackMessage?: string;
  feedbackColor?: string;
  noBorder?: boolean;
  defaultDropdownValue?: any;
  creatable?: boolean;
  isPortal?: boolean;
  customHeight?: string;
  menuIsOpen?: boolean;
  minWidthLabel?: string;
  backgroundColor?: string;
  fontSize?: string;
  valueContainerPadding?: string;
  singleValueMargin?: string;
  margin?: string;
  menuWidth?: string;
  width?: string;
  height?: string;
  displayWrapper?: string;
  overflowWidth?: string;
  closeMenuOnScroll?: EventListener;
  setRef?: any;
  dialogTextInput: boolean;
  noOptionsMessage?: string;
  loadingOptionsMessage?: string;
}

const DialogInput = (props: DialogInputProps) => {
  const { isDarkMode, theme: appTheme } = useTheme();
  const {
    id = '',
    loadOptions,
    isMulti,
    disabled,
    placeholder,
    onChange,
    onCreateOption,
    onFocus,
    small,
    isClearable,
    value,
    name,
    hasFeedback,
    feedbackMessage,
    feedbackColor = 'red',
    noBorder,
    defaultDropdownValue,
    menuIsOpen,
    customHeight,
    minWidthLabel = small ? '' : '200px',
    backgroundColor,
    fontSize,
    valueContainerPadding,
    singleValueMargin,
    margin,
    menuWidth = 'fit-content',
    width,
    displayWrapper,
    overflowWidth,
    setRef,
    isPortal,
    dialogTextInput,
    noOptionsMessage,
    loadingOptionsMessage,
  } = props;
  const customStyles = small
    ? createSmallStyles(
        isDarkMode,
        appTheme,
        customHeight,
        fontSize,
        noBorder,
        feedbackColor,
        hasFeedback,
        backgroundColor,
        disabled,
        minWidthLabel,
        singleValueMargin,
        overflowWidth,
        menuWidth,
      )
    : createNormalStyles(
        isDarkMode,
        appTheme,
        customHeight,
        fontSize,
        feedbackColor,
        hasFeedback,
        backgroundColor,
        disabled,
        minWidthLabel,
        valueContainerPadding,
        singleValueMargin,
        overflowWidth,
        menuWidth,
        width,
        margin,
      );

  const propsComponent = {
    name: name,
    className: 'react-select',
    styles: customStyles,
    isClearable: isClearable,
    onCreateOption: onCreateOption,
    onFocus: onFocus,
    value: value,
    theme: (selectTheme: any) => ({
      ...selectTheme,
      borderRadius: small ? borderRadiusPalette.dropDown.small : borderRadiusPalette.dropDown.normal,
      colors: {
        ...selectTheme.colors,
        primary25: isDarkMode ? appTheme.colorPalette.grey.grey8 : appTheme.colorPalette.turquoise.background,
        primary: feedbackColor && hasFeedback ? feedbackColor : appTheme.colorPalette.turquoise.light,
        neutral10: isDarkMode ? appTheme.colorPalette.grey.grey8 : appTheme.colorPalette.turquoise.background,
        neutral30: feedbackColor && hasFeedback ? feedbackColor : undefined,
        neutral80: isDarkMode ? appTheme.colorPalette.white : appTheme.colorPalette.black,
        neutral20: isDarkMode ? appTheme.colorPalette.grey.grey6 : appTheme.colorPalette.grey.grey5,
        neutral0: isDarkMode ? appTheme.colorPalette.grey.grey3 : appTheme.colorPalette.white,
      },
    }),
    isMulti: isMulti,
    isDisabled: disabled,
    placeholder: placeholder,
    defaultValue: defaultDropdownValue,
    menuIsOpen,
    menuPlacement: 'auto' as MenuPlacement,
    tabSelectsValue: false,
  };

  const [inputValue, setInputValue] = useState(value?.label || '');

  const handleBlur = () => {
    const val = (inputValue || '').trim();

    if (!val) {
      onChange?.(null);
      setInputValue('');
      return;
    }

    if (dialogTextInput) {
      // input type = 10 (free text dropdown) save what’s written
      onChange?.({ value: val, label: val });
      return;
    }

    setInputValue(value?.label || '');
  };

  const getNoOptionsMessage = noOptionsMessage ? () => noOptionsMessage : undefined;

  const getLoadingMessage = loadingOptionsMessage ? () => loadingOptionsMessage : undefined;

  return (
    <SelectWrapper display={displayWrapper} width={width}>
      <AsyncSelect
        key={`select_${id}_${value}`}
        onInputChange={(val, meta) => {
          if (meta.action === 'input-change') {
            setInputValue(val);
          }
          return val;
        }}
        onBlur={handleBlur}
        ref={setRef}
        loadOptions={loadOptions}
        defaultOptions
        cacheOptions
        menuPortalTarget={isPortal ? document.getElementById('react-select-portal') : null}
        noOptionsMessage={getNoOptionsMessage}
        loadingMessage={getLoadingMessage}
        onChange={(option: any) => {
          setInputValue(option?.label || '');
          onChange?.(option);
        }}
        {...propsComponent}
      />
      {hasFeedback && (
        <FeedbackText feedbackColor={feedbackColor} maxWidth={minWidthLabel}>
          {feedbackMessage}
        </FeedbackText>
      )}
    </SelectWrapper>
  );
};

export default DialogInput;
